import GenericTable from 'components/display/Table/Table';
import { BulkTableAction, Column, TableAction } from 'components/display/Table/types';
import { ReactNode, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTableComplete } from '../hooks/useTableComplete';
import TableProvider from '../TableProvider';
import { TableApiConfig } from '../types';
import { BooleanFacet, Facet, FacetQueryCommand, createFacetLabelCache } from '../facetFilters';
import { ListExportParams } from '../exportCsv';
import { FacetActiveFilters, FacetFilterDrawer } from './FacetFilterPanel';
import {
  ColumnPicker,
  applyColumnPrefs,
  moveColumnBefore,
  useColumnPrefs
} from './ColumnCustomization';
import { OnCreateConfig } from './types';

interface ServerTableProps<T extends Record<string, any>> {
  // Configuration API (obligatoire)
  apiConfig: TableApiConfig<T>;

  // Colonnes (obligatoire)
  columns: Column<T>[];

  // En-tête du tableau
  title?: {
    label: string;
    iconKey?: string;
    description?: string;
  };

  // Actions
  actions?: {
    row?: TableAction<T>[];
    bulk?: BulkTableAction<T>[];
    create?: OnCreateConfig;
  };

  // Export CSV du résultat courant : reçoit la commande courante (recherche + filtres +
  // tri) de la liste ; la vue mappe les filtres et appelle la route .../csv
  onExport?: (params: ListExportParams) => void | Promise<void>;

  // Tri serveur (sortBy/sortDirection envoyés au back) : seules les colonnes avec
  // sortKey affichent le contrôle de tri ; changement de tri => page 1 + refetch liste
  serverSorting?: boolean;

  // Personnalisation des colonnes (sélecteur de visibilité + drag & drop des en-têtes),
  // persistée en cache navigateur sous la storageKey donnée
  columnCustomization?: {
    storageKey: string;
    /** Colonnes masquées par défaut (tant que l'utilisateur n'a rien personnalisé). */
    defaultHidden?: string[];
  };

  // Centre par défaut le contenu des colonnes (en-tête + cellules) qui ne fixent pas
  // explicitement leur `align`
  centerColumns?: boolean;

  // Expansion de ligne : contenu déplié au clic (chargé à la demande par le composant fourni)
  renderExpanded?: (row: T) => React.ReactNode;

  // Sélection multiple
  selectable?: boolean;

  // Configuration de pagination
  pageSize?: number;
  pageSizeOptions?: number[];
  showPaginationInfo?: boolean;

  // Configuration des filtres
  enableFilters?: boolean;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  autoFetchFiltersOnMount?: boolean;

  // Panneau à facettes piloté par le back ({domaine}/liste/filtres) : le bouton
  // « Filtrer » natif ouvre un drawer latéral générique
  facets?: {
    fetchFacets: (command: FacetQueryCommand) => Promise<{ facets: Facet[] }>;
    /**
     * Cases à cocher booléennes ajoutées au drawer en plus des facettes back
     * (ex. isTest). La valeur vit dans le state de filtres et est propagée à la liste,
     * aux facettes et à l'export via mapFacetFiltersToFiltersDto.
     */
    booleanFacets?: BooleanFacet[];
  };

  // Filtres appliqués d'entrée et cible du reset (ex. { isTest: false }) : le state de
  // filtres démarre avec ces valeurs et « Réinitialiser » y revient (pas objet vide)
  initialFilters?: Record<string, any>;

  // Classe CSS dynamique par ligne selon la donnée (ex. surbrillance des lignes de test)
  getRowClassName?: (row: T, index: number) => string;

  // Chargement initial
  autoFetchOnMount?: boolean;

  // Refresh externe (incrémenter pour refetch sans remonter le composant)
  refreshTrigger?: number;

  // Personnalisation du style
  styling?: {
    containerClass?: string;
    tableClass?: string;
    headerClass?: string;
    rowClass?: string;
    evenRowClass?: string;
    hoverClass?: string;
  };

  // Skeleton loader
  skeletonRowCount?: number;

  // Rendu personnalisé
  renderCustomHeader?: () => ReactNode;
  renderCustomFilters?: () => ReactNode;
  renderEmptyState?: () => ReactNode;

  // Callbacks
  onRowClick?: (row: T, index: number) => void;
  onSelectionChange?: (selectedIds: Set<string | number>) => void;
}

/**
 * Composant interne qui utilise le contexte du TableProvider
 */
function ServerTableContent<T extends Record<string, any> & { id: string | number }>({
  columns,
  title,
  actions,
  selectable = false,
  pageSizeOptions = [10, 15, 20, 25, 50],
  showPaginationInfo = true,
  enableFilters = true,
  enableSearch = true,
  searchPlaceholder,
  facets,
  onExport,
  serverSorting = false,
  columnCustomization,
  centerColumns = false,
  renderExpanded,
  getRowClassName,
  styling,
  skeletonRowCount = 5,
  renderCustomHeader,
  renderCustomFilters,
  renderEmptyState,
  onRowClick,
  onSelectionChange
}: Omit<ServerTableProps<T>, 'apiConfig' | 'pageSize' | 'initialFilters' | 'autoFetchOnMount' | 'autoFetchFiltersOnMount'>) {

  const {
    data,
    loading,
    error,
    pagination,
    search,
    filtering,
    selection,
    sort
  } = useTableComplete<T>();

  const [isFacetDrawerOpen, setIsFacetDrawerOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Libellés des options/dimensions déjà vus, partagés entre le drawer et les chips
  const facetLabelCacheRef = useRef(createFacetLabelCache());

  // Export CSV : rejoue la commande courante (recherche + filtres + tri), avec chargement
  const handleExport = async () => {
    if (!onExport) return;
    setIsExporting(true);
    try {
      await onExport({
        searchString: search.searchString || undefined,
        sortBy: sort.sortField ?? null,
        sortDirection: sort.sortDirection ?? undefined,
        filters: filtering.filters
      });
    } catch (error: any) {
      toast.error(error?.message || "Échec de l'export CSV.");
    } finally {
      setIsExporting(false);
    }
  };

  // Nombre de valeurs actives (badge du bouton « Filtrer »). Un booléen ne compte que
  // s'il s'écarte de la neutralité (true) : isTest=false (défaut) ne gonfle pas le badge.
  const activeFacetValuesCount = facets
    ? Object.entries(filtering.filters).reduce(
        (acc, [key, value]) =>
          acc +
          (key === 'period'
            ? 1
            : Array.isArray(value)
              ? value.length
              : value === true
                ? 1
                : 0),
        0
      )
    : 0;

  // Callback pour les changements de sélection
  if (onSelectionChange && selection.selectedItems) {
    // Utiliser un useEffect si nécessaire pour éviter les effets de bord
    // useEffect(() => {
    //   onSelectionChange(selection.selectedItems);
    // }, [selection.selectedItems]);
  }

  // En tri serveur, seules les colonnes avec sortKey sont triables ;
  // centerColumns centre les colonnes qui ne fixent pas leur align
  const enrichedColumns = columns.map(col => ({
    ...col,
    ...(serverSorting && { sortable: !!col.sortKey }),
    ...(centerColumns && !col.align && { align: 'center' as const })
  }));

  // Personnalisation des colonnes : visibilité + ordre, persistés par tableau
  const { prefs: columnPrefs, updatePrefs: updateColumnPrefs } = useColumnPrefs(
    columnCustomization?.storageKey,
    columnCustomization?.defaultHidden
  );
  const displayColumns = columnCustomization
    ? applyColumnPrefs(enrichedColumns, columnPrefs)
    : enrichedColumns;

  // Enrichir les actions de ligne avec onClick si nécessaire
  const enrichedRowActions = actions?.row?.map(action => ({
    ...action,
    onClick: (row: T, index: number) => {
      action.onClick?.(row, index);
      onRowClick?.(row, index);
    }
  }));

  return (
    <>
      {renderCustomHeader?.()}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          <p className="font-medium">Erreur</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {renderCustomFilters?.()}

      <GenericTable
        config={{
          columns: displayColumns,
          data: data as T[],
          title,
          actions: {
            ...actions,
            row: enrichedRowActions,
            ...(onExport && {
              export: {
                label: isExporting ? 'Export…' : 'Exporter CSV',
                onClick: handleExport,
                loading: isExporting
              }
            })
          },
          selectable,
          loading,
          skeletonRowCount,
          styling,
          getRowClassName,
          // Colonne Actions centrée quand les colonnes sont centrées
          centerActions: centerColumns,
          ...(renderExpanded && { expandable: { render: renderExpanded } }),

          // Tri serveur : clic colonne -> contexte (sortBy/sortDirection) -> refetch
          ...(serverSorting && {
            serverSort: {
              field: sort.sortField,
              direction: sort.sortDirection,
              onSort: sort.toggleSort
            }
          }),

          // Personnalisation des colonnes : sélecteur dans la toolbar + drag & drop
          ...(columnCustomization && {
            toolbarExtra: (
              <ColumnPicker
                columns={enrichedColumns}
                prefs={columnPrefs}
                onChange={updateColumnPrefs}
              />
            ),
            columnReorder: {
              onMove: (fromKey: string, toKey: string) =>
                updateColumnPrefs(
                  moveColumnBefore(enrichedColumns, columnPrefs, fromKey, toKey)
                )
            }
          }),

          // Configuration de la pagination serveur
          pagination: {
            enabled: true,
            currentPage: pagination.currentPage,
            totalPages: pagination.totalPages,
            totalItems: pagination.totalItems,
            pageSize: pagination.pageSize,
            onPageChange: pagination.setCurrentPage,
            onPageSizeChange: pagination.setPageSize,
            pageSizeOptions,
            showInfo: showPaginationInfo
          },

          // Configuration des filtres et recherche
          filters: {
            enabled: enableFilters,
            searchable: enableSearch,
            searchString: search.searchString,
            onSearch: search.setSearchString,
            searchPlaceholder: searchPlaceholder || 'Rechercher...',
            // Panneau à facettes : le bouton « Filtrer » natif ouvre le drawer
            ...(facets && {
              onFilterClick: () => setIsFacetDrawerOpen(true),
              activeFiltersCount: activeFacetValuesCount,
              renderActiveFilters: () => (
                <FacetActiveFilters
                  labelCache={facetLabelCacheRef.current}
                  booleanFacets={facets.booleanFacets}
                />
              )
            })
          }
        }}
      />

      {facets && (
        <FacetFilterDrawer
          isOpen={isFacetDrawerOpen}
          onClose={() => setIsFacetDrawerOpen(false)}
          fetchFacets={facets.fetchFacets}
          booleanFacets={facets.booleanFacets}
          labelCache={facetLabelCacheRef.current}
        />
      )}

      {!loading && data.length === 0 && renderEmptyState && (
        <div className="mt-4">
          {renderEmptyState()}
        </div>
      )}
    </>
  );
}

/**
 * ServerTable - Composant principal pour créer facilement des tableaux avec API
 * 
 * @example
 * ```tsx
 * <ServerTable<User>
 *   apiConfig={{
 *     fetchData: async (params) => {
 *       const response = await fetch('/api/users', {
 *         method: 'POST',
 *         body: JSON.stringify(params)
 *       });
 *       return response.json();
 *     }
 *   }}
 *   columns={[
 *     { key: 'name', label: 'Nom', sortable: true },
 *     { key: 'email', label: 'Email' }
 *   ]}
 *   title={{ label: 'Utilisateurs' }}
 *   selectable
 * />
 * ```
 */
export function ServerTable<T extends Record<string, any> & { id: string | number }>({
  apiConfig,
  pageSize = 10,
  initialFilters,
  autoFetchOnMount = true,
  autoFetchFiltersOnMount = true,
  refreshTrigger,
  ...contentProps
}: ServerTableProps<T>) {

  return (
    <TableProvider<T>
      apiConfig={apiConfig}
      initialPageSize={pageSize}
      initialFilters={initialFilters}
      autoFetchOnMount={autoFetchOnMount}
      autoFetchFiltersOnMount={autoFetchFiltersOnMount}
      refreshTrigger={refreshTrigger}
    >
      <ServerTableContent<T> {...contentProps} />
    </TableProvider>
  );
}

export default ServerTable;
